/**
 * Bundle 139 P0-1 - Age verification pending admin queue.
 *
 * Super-admin surface for the `pending_review` bucket (users
 * whose grandfathering-modal DOB came back <18). Every row action
 * requires a confirmation modal + optional note.
 *
 * Guardrails: no em/en dashes; no eslint-disable; no photo/image
 * in HTML alt attributes.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { RefreshCw, Shield, AlertOctagon, RotateCcw, ArrowUp } from 'lucide-react';

const STATUS_LABEL = {
  pending_review: 'Pending review',
  reset_pending_reentry: 'Reset',
  terminated_minor: 'Terminated',
  escalated: 'Escalated',
};

const STATUS_TONE = {
  pending_review: 'bg-amber-100 text-amber-800 border border-amber-200',
  reset_pending_reentry: 'bg-neutral-100 text-neutral-700 border border-neutral-200',
  terminated_minor: 'bg-accent-100 text-accent-800 border border-accent-200',
  escalated: 'bg-secondary-100 text-secondary-800 border border-secondary-200',
};

const AdminAgeVerificationPendingPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/age-verification-pending');
      setRows(data?.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load pending list');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="admin-age-verification-pending-page">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-secondary-800 mb-1">
            Stripe compliance * Bundle 139
          </div>
          <h1 className="font-display text-3xl font-bold text-primary-900 flex items-center gap-2">
            <Shield className="h-6 w-6" /> Age verification pending
          </h1>
          <p className="text-neutral-600 text-sm mt-1 max-w-2xl">
            Users whose grandfathering-modal DOB came back under 18. Support processes each row: reset the DOB, confirm the minor status and terminate, or escalate for review.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="age-verification-refresh-btn">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="age-verification-pending-table">
            <thead className="bg-neutral-50 text-neutral-700">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Email</th>
                <th className="text-left px-4 py-3 font-medium">Entered DOB (year)</th>
                <th className="text-left px-4 py-3 font-medium">Entered at</th>
                <th className="text-left px-4 py-3 font-medium">Source</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-neutral-500" data-testid="age-verification-pending-empty">
                    No pending age verification cases.
                  </td>
                </tr>
              ) : rows.map((r) => (
                <tr key={r.id} data-testid={`age-verification-row-${r.id}`} className="border-t border-neutral-100">
                  <td className="px-4 py-3 font-mono text-xs text-primary-900">{r.email || r.id}</td>
                  <td className="px-4 py-3">{r.reported_dob_year || '-'}</td>
                  <td className="px-4 py-3 text-neutral-600 text-xs">
                    {r.age_gate_reported_at ? new Date(r.age_gate_reported_at).toLocaleString() : '-'}
                  </td>
                  <td className="px-4 py-3 text-neutral-600 text-xs">{r.age_gate_reported_method || '-'}</td>
                  <td className="px-4 py-3">
                    <span
                      data-testid={`age-verification-status-${r.id}`}
                      className={`inline-block rounded px-2 py-0.5 text-[11px] font-medium ${STATUS_TONE[r.age_gate_status] || 'bg-neutral-100 text-neutral-700'}`}
                    >
                      {STATUS_LABEL[r.age_gate_status] || r.age_gate_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {r.age_gate_status === 'pending_review' ? (
                      <div className="inline-flex gap-1.5">
                        <Button size="sm" variant="outline"
                                    onClick={() => setConfirmAction({ user: r, action: 'reset' })}
                                    data-testid={`age-verification-reset-btn-${r.id}`}>
                          <RotateCcw className="h-3 w-3 mr-1" /> Reset
                        </Button>
                        <Button size="sm" variant="outline"
                                    onClick={() => setConfirmAction({ user: r, action: 'escalate' })}
                                    data-testid={`age-verification-escalate-btn-${r.id}`}>
                          <ArrowUp className="h-3 w-3 mr-1" /> Escalate
                        </Button>
                        <Button size="sm"
                                    onClick={() => setConfirmAction({ user: r, action: 'terminate' })}
                                    className="bg-accent-700 hover:bg-accent-800 text-white"
                                    data-testid={`age-verification-terminate-btn-${r.id}`}>
                          <AlertOctagon className="h-3 w-3 mr-1" /> Terminate
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-neutral-500">No actions</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <ConfirmActionDialog
        open={!!confirmAction}
        userRow={confirmAction?.user}
        action={confirmAction?.action}
        onClose={() => setConfirmAction(null)}
        onDone={() => { setConfirmAction(null); load(); }}
      />
    </div>
  );
};

const ACTION_COPY = {
  reset: {
    title: 'Reset and re-request DOB',
    body: 'Clears the DOB fields on the user. On next login they will see the grandfathering modal again. Use when the reported DOB looks like a typo or the user asked for another chance.',
    cta: 'Reset',
    ctaClass: 'bg-primary-800 hover:bg-primary-900 text-white',
  },
  escalate: {
    title: 'Escalate for review',
    body: 'Adds this case to the escalations queue with your note. The user status changes to Escalated. No account changes are made.',
    cta: 'Escalate',
    ctaClass: 'bg-secondary-700 hover:bg-secondary-800 text-white',
    requireNote: true,
  },
  terminate: {
    title: 'Confirm minor and terminate',
    body: 'Soft-deletes the user, voids all active tickets with status voided_minor, writes to the age_gate_terminations audit collection. Refunds are NOT auto-issued. Support processes refunds manually per event refund policy. Requires the AGE_GATE_HARD_TERMINATION_ENABLED flag ON.',
    cta: 'Terminate account',
    ctaClass: 'bg-accent-700 hover:bg-accent-800 text-white',
  },
};

const ConfirmActionDialog = ({ open, userRow, action, onClose, onDone }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const meta = action ? ACTION_COPY[action] : null;
  useEffect(() => { if (open) setNote(''); }, [open]);
  if (!open || !meta || !userRow) return null;

  const run = async () => {
    setBusy(true);
    try {
      const path = `/admin/age-verification-pending/${userRow.id}/${action}`;
      const body = { note: note.trim() };
      if (action === 'escalate' && !note.trim()) {
        toast.error('Escalation requires a note.');
        setBusy(false);
        return;
      }
      const { data } = await api.post(path, body);
      if (data?.dry_run) {
        toast.warning(`Dry run: ${data.reason}`, { duration: 6000 });
      } else {
        toast.success(`${meta.title} complete.`);
      }
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || `${meta.title} failed`);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid={`age-verification-confirm-dialog-${action}`}>
        <DialogHeader>
          <DialogTitle>{meta.title}</DialogTitle>
          <DialogDescription>
            <span className="font-mono text-xs text-neutral-600">{userRow.email || userRow.id}</span>
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm text-primary-800">{meta.body}</p>
        <div className="space-y-1">
          <Label htmlFor="age-verification-note">
            Note {meta.requireNote ? '' : '(optional)'}
          </Label>
          <Textarea
            id="age-verification-note"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid={`age-verification-note-${action}`}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}
                      data-testid={`age-verification-cancel-${action}`}>
            Cancel
          </Button>
          <Button onClick={run} disabled={busy || (meta.requireNote && !note.trim())}
                      className={meta.ctaClass}
                      data-testid={`age-verification-confirm-${action}`}>
            {busy ? 'Working...' : meta.cta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminAgeVerificationPendingPage;
